import { useToastContext } from '@/contexts/toastContext';
import useApiFetch from '@/hooks/useApiFetch';
import useApiSubmit from '@/hooks/useApiSubmit';
import useProfile from '@/hooks/useProfileV2';
import candidateServices from '@/services/candidates';
import interviewServices, {
  AddReviewerToInterviewType,
  DeleteTeamInterviewReviewerType,
} from '@/services/interviews';
import { FetchApiIds } from '@/types';
import AssignedInterviewReviewsManagerComponent from './AssignedInterviewReviewsManager.component';

type AssignedInterviewReviewsManagerType = {
  isTeamInterview: boolean;
  interviewTechnicalChallengeId: string;
  teamInterviewId?: string;
  selectedTechnicalChallengeId: string;
  onReviewersChanged?: () => void;
};

export default function AssignedInterviewReviewsManager({
  isTeamInterview,
  interviewTechnicalChallengeId,
  selectedTechnicalChallengeId,
  teamInterviewId,
  ...props
}: AssignedInterviewReviewsManagerType): JSX.Element {
  const toast = useToastContext();
  const { profile } = useProfile();
  const currentUserId = profile.id || '';

  const isProfileReady = !!currentUserId && currentUserId !== 'test';
  const currentUserResponse = useApiFetch(
    [FetchApiIds.CANDIDATE_BY_ID, currentUserId],
    async () => candidateServices.getCandidateByID(currentUserId),
    { retry: 1, enabled: isProfileReady }
  );

  const reviewersSuggestionsResponse = useApiFetch(
    [FetchApiIds.TECHNICAL_CHALLENGE_SUGGESTIONS, selectedTechnicalChallengeId],
    async () => interviewServices.getTechnicalChallengesSuggestions(selectedTechnicalChallengeId),
    {
      enabled: !!selectedTechnicalChallengeId,
    }
  );

  const allReviewersResponse = useApiFetch(
    [FetchApiIds.ALL_REVIEWERS],
    interviewServices.getAllTechnicalChallengesReviewers
  );

  const technicalChallengeReviewers = useApiFetch(
    [FetchApiIds.TECHNICAL_CHALLENGE_REVIEWERS, interviewTechnicalChallengeId],
    async () => interviewServices.getTechnicalChallengeReviewers(interviewTechnicalChallengeId),
    { enabled: !!interviewTechnicalChallengeId }
  );

  const teamInterviewReviewers = useApiFetch(
    [FetchApiIds.TEAM_INTERVIEW_REVIEWERS, teamInterviewId],
    async () => interviewServices.getTeamInterviewReviewers(teamInterviewId ?? ''),
    { enabled: !!isTeamInterview }
  );

  const addTechnicalChallengeReviewerMutation = useApiSubmit<void, AddReviewerToInterviewType>(
    interviewServices.addTechnicalChallengeReviewer
  );

  const addTeamInterviewReviewerMutation = useApiSubmit<void, AddReviewerToInterviewType>(
    interviewServices.addTeamInterviewReviewer
  );

  const deleteTechnicalChallengeReviewerMutation = useApiSubmit<void, string>(
    interviewServices.deleteTechnicalChallengeReviewer
  );

  const deleteTeamInterviewReviewerMutation = useApiSubmit<void, DeleteTeamInterviewReviewerType>(
    interviewServices.deleteTeamInterviewReviewer
  );

  const reviewers = technicalChallengeReviewers.data ?? teamInterviewReviewers.data;

  const assignedUsers = reviewers?.map((reviewer): any => ({
    firstName: reviewer?.interviewReviewer?.person?.firstName,
    lastName: reviewer?.interviewReviewer?.person?.lastName,
    pictureUrl: reviewer?.interviewReviewer?.person?.pictureUrl,
    id: reviewer?.id,
    reviewerId: reviewer?.interviewReviewer?.id,
  }));

  const formatReviewerResponse = (reviewer): any => {
    return {
      firstName: reviewer?.person?.firstName,
      lastName: reviewer?.person?.lastName,
      pictureUrl: reviewer?.person?.pictureUrl,
      id: reviewer?.id,
    };
  };

  const filterByAssingedReviewers = (reviewer): boolean => {
    return !assignedUsers?.some((assinedReviewer) => {
      return assinedReviewer.reviewerId === reviewer.id;
    });
  };

  const isUserAReviewer = (currentUserResponse.data?.candidate?.mainTechStack ?? []).some(
    (skill) => skill?.willingToReview
  );

  const isUserOnAssignedReviewers = !assignedUsers?.some((assinedReviewer) => {
    return assinedReviewer.reviewerId === currentUserId;
  });

  const refetchReviewers = (): void => {
    allReviewersResponse.refetch();
    if (!reviewersSuggestionsResponse.isIdle) {
      reviewersSuggestionsResponse.refetch();
    }
    if (!technicalChallengeReviewers.isIdle) {
      technicalChallengeReviewers.refetch();
    }
    if (!teamInterviewReviewers.isIdle) {
      teamInterviewReviewers.refetch();
    }
  };

  const filterInvalidReviewers = (reviewer): boolean => {
    return !!reviewer?.person?.firstName;
  };

  const allReviewers = allReviewersResponse.data
    ?.filter(filterInvalidReviewers)
    ?.map(formatReviewerResponse)
    .filter(filterByAssingedReviewers);

  const reviewersSuggestions = reviewersSuggestionsResponse.data
    ?.filter(filterInvalidReviewers)
    ?.map(formatReviewerResponse)
    .filter(filterByAssingedReviewers);

  const onRemoveAssignedUser = async (id: string): Promise<void> => {
    try {
      if (isTeamInterview) {
        await deleteTeamInterviewReviewerMutation.mutateAsync({
          teamInterviewId: teamInterviewId ?? '',
          teamInterviewReviewerId: id,
        });
      } else {
        await deleteTechnicalChallengeReviewerMutation.mutateAsync(id);
      }

      refetchReviewers();
      props.onReviewersChanged?.();
      toast.showSuccessFeedback('The reviewer has been removed.');
    } catch (err) {
      toast.showErrorFeedback('There was a problem removing the reviewer.');
    }
  };

  const onAssign = async (interviewReviewerId: string): Promise<void> => {
    try {
      const methodToCall = isTeamInterview
        ? addTeamInterviewReviewerMutation
        : addTechnicalChallengeReviewerMutation;
      const interviewId = isTeamInterview ? teamInterviewId : interviewTechnicalChallengeId;
      await methodToCall.mutateAsync({
        interviewId: interviewId ?? '',
        interviewReviewerId,
      });
      refetchReviewers();
      props.onReviewersChanged?.();
      toast.showSuccessFeedback('The reviewer has been added.');
    } catch (err) {
      toast.showErrorFeedback('There was a problem adding the reviewer.');
    }
  };

  const onAssignToMyself = async (): Promise<void> => {
    try {
      await addTechnicalChallengeReviewerMutation.mutateAsync({
        interviewId: interviewTechnicalChallengeId,
        interviewReviewerId: currentUserId,
      });
      refetchReviewers();
      props.onReviewersChanged?.();
      toast.showSuccessFeedback('The reviewer has been added.');
    } catch (err) {
      toast.showErrorFeedback('There was a problem adding the reviewer.');
    }
  };

  return (
    <AssignedInterviewReviewsManagerComponent
      {...props}
      isUserReviewer={isUserAReviewer && isUserOnAssignedReviewers}
      onAssign={onAssign}
      onAssignToMyself={onAssignToMyself}
      onRemoveAssignedUser={onRemoveAssignedUser}
      assignedUsers={assignedUsers ?? []}
      reviewersSuggestions={reviewersSuggestions ?? []}
      allReviewers={allReviewers ?? []}
    />
  );
}
